import { useMemo } from "react";
import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { defineMessages, useIntl } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { classNames, useAppConfig } from "@ct-react/core";
import { LocaleLink, useLocaleContext } from "@ct-react/locale";
import { extractCmsContent, Image } from "@shared/graphql";
import { SkeletonableProps } from "../../tools/components";
import { Wrapper } from "../../components/layout/wrapper";
import SeoMetas from "../../components/layout/seo-metas";
import PageHero from "../../components/layout/page-elements/page-hero";
import RatioImage from "../../components/medias/ratio-image";
import bgPath from "../../assets/bg-images/hero-makeover.webp";
import "./index.scss";

const transDefs = defineMessages({
  title: { id: "makeovers.index.title", defaultMessage: "Projets de rénovation" }
});

const PROJECTS_LIST_GQL_DATA = gql`
  query GalleryProjects($templateId: UUID!) {
    projects: cmsPages(templateId: $templateId, asSummary: true) {
      id createdAt data
    }
  }
`;

type Project = {
  id: string;
  href: string;
  title: string;
  lead: string;
  image: Image;
}

const ProjectCard = ({ loading, className, data }: SkeletonableProps<Project>) => {

  const wrapperClasses = classNames("project-card", className);

  if (loading || !data)
    return (
      <Skeleton className={wrapperClasses} style={{ aspectRatio: "3/2" }} />);

  return (
    <article className={wrapperClasses}>
      <LocaleLink to={data.href}>
        <div className="picture-wrap">
          <RatioImage source={data.image.assets} />
        </div>
        <h2>{data.title}</h2>
        <div className="lead">
          <p className="lead-content">{data.lead}</p>
        </div>
      </LocaleLink>
    </article>);
}

const MakeoverIndexPage = () => {

  const intl = useIntl();
  const { locale } = useLocaleContext();
  const { options: { cmsGalleryTemplateId: templateId } } = useAppConfig();

  const { loading, data } = useQuery<{ projects: any[] }>(PROJECTS_LIST_GQL_DATA, {
    ssr: true,
    variables: { templateId }
  });

  const items = useMemo<Project[] | undefined>(() => {
    if (!data?.projects) return undefined;
    return [ ...data.projects ]
      .sort(({ createdAt: a }, { createdAt: b }) => new Date(b).getTime() - new Date(a).getTime())
      .map(({ id, data }) => ({ id, data: extractCmsContent(data, locale) }))
      .map(({ id, data }) => ({
        id,
        href: `/renovation/makeovers/${id}`,
        title: data["title"],
        lead: data["lead"],
        image: data["main-image"]
      }))
  }, [ data, locale ]);

  return (
    <>
      <SeoMetas title={`${intl.formatMessage(transDefs.title)} | EV Real Estate`} />
      <PageHero title={intl.formatMessage(transDefs.title)} background={bgPath} />
      <Wrapper.Centered as="main" className="makeovers content">
        {(items || Array.from({ length: 2 })).map((project: any, index: number) => (
          <ProjectCard key={project?.id || index} loading={loading} data={project} />)
        )}
      </Wrapper.Centered>
    </>);
}

export default MakeoverIndexPage;
